import { queryAllAcrossRoots } from './domUtils.js';

const PENDING_PLACEHOLDER = 'Yanıt bekleniyor...';
type ComposerState = { disabled: boolean; placeholder: string };
type TypingDrawer = {
  showTypingIndicator?: (...args: unknown[]) => unknown;
  resumeTypingIndicator?: (...args: unknown[]) => unknown;
  removeTypingIndicator?: (...args: unknown[]) => unknown;
};
type PendingGuardChat = { _drawer?: TypingDrawer };

function isVisibleElement(element: Element | null | undefined): element is HTMLElement {
  if (typeof HTMLElement === 'undefined' || !(element instanceof HTMLElement)) {
    return Boolean(element);
  }

  if (element.hidden || element.getAttribute('aria-hidden') === 'true') {
    return false;
  }

  const style = typeof window === 'undefined' ? undefined : window.getComputedStyle?.(element);
  if (style && (style.display === 'none' || style.visibility === 'hidden')) {
    return false;
  }

  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

export function mountComposerPendingGuard(chat?: unknown): () => void {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
    return () => {};
  }

  const previousStates = new WeakMap<HTMLTextAreaElement, ComposerState>();
  const widget = chat as PendingGuardChat | undefined;
  let typingActive = false;
  let patchedDrawer: TypingDrawer | undefined;
  let restoreTypingMethods = () => {};
  const observerOptions: MutationObserverInit = {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-hidden', 'class', 'disabled', 'hidden', 'style'],
  };

  const patchDrawer = () => {
    const drawer = widget?._drawer;
    if (!drawer || drawer === patchedDrawer) return;
    restoreTypingMethods();
    patchedDrawer = drawer;
    const originalShow = drawer.showTypingIndicator;
    const originalResume = drawer.resumeTypingIndicator;
    const originalRemove = drawer.removeTypingIndicator;
    if (typeof originalShow !== 'function' || typeof originalRemove !== 'function') {
      restoreTypingMethods = () => {};
      return;
    }
    drawer.showTypingIndicator = (...args: unknown[]) => {
      // The SDK show method removes the previous indicator before creating one.
      const result = originalShow.apply(drawer, args);
      typingActive = true;
      sync();
      return result;
    };
    if (typeof originalResume === 'function') {
      drawer.resumeTypingIndicator = (...args: unknown[]) => {
        const result = originalResume.apply(drawer, args);
        typingActive = true;
        sync();
        return result;
      };
    }
    drawer.removeTypingIndicator = (...args: unknown[]) => {
      const result = originalRemove.apply(drawer, args);
      typingActive = false;
      sync();
      return result;
    };
    restoreTypingMethods = () => {
      drawer.showTypingIndicator = originalShow;
      if (originalResume) drawer.resumeTypingIndicator = originalResume;
      drawer.removeTypingIndicator = originalRemove;
      if (patchedDrawer === drawer) patchedDrawer = undefined;
    };
  };

  const sync = () => {
    patchDrawer();
    for (const input of queryAllAcrossRoots(
      '[data-gengage-part="chat-input"]'
    ) as HTMLTextAreaElement[]) {
      const area = input.closest('[data-gengage-part="chat-input-area"]');
      const send = area?.querySelector<HTMLElement>('[data-gengage-part="chat-send"]');
      const sendStop = Boolean(
        send?.classList.contains('gengage-chat-send--stop') && isVisibleElement(send)
      );
      const pending = sendStop || typingActive;

      if (pending) {
        if (!previousStates.has(input)) {
          previousStates.set(input, {
            disabled: false,
            placeholder: input.placeholder,
          });
        }
        if (!input.disabled) input.disabled = true;
        input.placeholder = PENDING_PLACEHOLDER;
      } else if (previousStates.has(input)) {
        const previous = previousStates.get(input);
        input.disabled = previous?.disabled ?? false;
        input.placeholder = previous?.placeholder ?? '';
        previousStates.delete(input);
      }
    }
  };

  const observer = new MutationObserver(sync);
  observer.observe(document.documentElement, observerOptions);
  sync();

  return () => {
    observer.disconnect();
    restoreTypingMethods();
    for (const input of queryAllAcrossRoots(
      '[data-gengage-part="chat-input"]'
    ) as HTMLTextAreaElement[]) {
      if (previousStates.has(input)) {
        const previous = previousStates.get(input);
        input.disabled = previous?.disabled ?? false;
        input.placeholder = previous?.placeholder ?? '';
      }
    }
  };
}
